import React from 'react';
import { Link } from 'react-router-dom';
import { isOrgAdminRole, isOrgManagerRole, normalizeRoleKeyForPermissions } from '../utils/iamRoles';

const DashboardAlerts = ({ stats, userRole }) => {
  const alerts = [];
  const matrixRole = normalizeRoleKeyForPermissions(userRole);

  // Alerts for executive / finance lead, org admin (legacy président / trésorier → matrixRole)
  if (isOrgAdminRole(userRole) || matrixRole === 'executive' || matrixRole === 'finance_lead') {
    if (stats?.pending_transactions_count > 0) {
      alerts.push({
        type: 'warning',
        title: 'Transactions en attente d\'approbation',
        message: `${stats.pending_transactions_count} transaction(s) nécessite(nt) votre approbation`,
        link: '/my-fund/financial',
        icon: (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
          </svg>
        )
      });
    }

    if (stats?.unpaid_contributions_count > 0) {
      alerts.push({
        type: 'info',
        title: 'Cotisations en attente',
        message: `${stats.unpaid_contributions_count} cotisation(s) en attente de paiement`,
        link: '/contributions',
        icon: (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
          </svg>
        )
      });
    }
  }

  // Alerts for operations lead / executive, org admin, org manager (legacy secrétaire → matrixRole)
  if (isOrgAdminRole(userRole) || isOrgManagerRole(userRole) || matrixRole === 'executive' || matrixRole === 'operations_lead') {
    if (stats?.pending_meetings_count > 0) {
      alerts.push({
        type: 'info',
        title: 'Réunions non publiées',
        message: `${stats.pending_meetings_count} rapport(s) de réunion en attente de publication`,
        link: '/calendar',
        icon: (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
          </svg>
        )
      });
    }
  }

  if (alerts.length === 0) {
    return null;
  }

  const alertStyles = {
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    danger: 'bg-red-50 border-red-200 text-red-800',
    success: 'bg-green-50 border-green-200 text-green-800'
  };

  return (
    <div className="space-y-3">
      {alerts.map((alert, index) => (
        <Link
          key={index}
          to={alert.link}
          className={`block p-4 rounded-lg border-2 ${alertStyles[alert.type]} hover:shadow-md transition-all`}
        >
          <div className="flex items-center gap-3">
            <div className={`flex-shrink-0 ${alert.type === 'warning' ? 'text-yellow-600' : alert.type === 'danger' ? 'text-red-600' : 'text-blue-600'}`}>
              {alert.icon}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{alert.title}</h3>
              <p className="text-sm opacity-90">{alert.message}</p>
            </div>
            <svg className="h-5 w-5 opacity-50" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default DashboardAlerts;
